import { Badge } from '@change/ds-core-ui';
import { ErrorMessage, TextField } from '@change/ds-forms';
import { HeartFilledIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useAmountSelector } from './hooks/useAmountSelector';

const BUTTON_BASE_CLASS = [
	'flex-1 relative cursor-pointer rounded-lg border px-2 py-4',
	'text-center text-size-body transition-colors',
	'text-typography-accent-primary',
].join(' ');

const BUTTON_DEFAULT_CLASS = `${BUTTON_BASE_CLASS} border-stroke-accent-primary-weak `;
const BUTTON_SELECTED_CLASS = `${BUTTON_BASE_CLASS} outline-stroke-info-strong bg-fill-accent-primary-weaker outline-2 border-0`;

const CURRENCY_SYMBOL_POSITION_CLASS = {
	before:
		'[&>div>div>span]:text-typography-accent-primary [&>div>div>span]:absolute [&>div>div>span]:top-7! [&>div>div>span]:left-3! [&>div>div>span]:w-4!',
	after:
		'[&>div>div>span]:text-typography-accent-primary [&>div>div>span]:absolute [&>div>div>span]:top-7! [&>div>div>span]:right-3! [&>div>div>span]:w-4!',
};

export const SimpleAmountSelector: React.FC = () => {
	const {
		data: {
			buttonAmounts,
			selection,
			customAmount,
			customAmountLabel,
			currencySymbol,
			currencySymbolPosition,
			amountErrorText,
		},
		actions: { handleAmountChange, handleCustomAmountFocus, handleCustomAmountChange },
	} = useAmountSelector();

	const isGrid = buttonAmounts.length > 3;

	return (
		<div>
			<div
				role="radiogroup"
				aria-label=""
				className={isGrid ? 'grid grid-cols-3 gap-2' : 'flex flex-row gap-2'}
				data-qa="promotion-impression-amount-selector"
			>
				{buttonAmounts.map(({ value, valueDisplay, suggestedLabel }) => (
					<button
						data-qa="suggested-amount-button"
						key={value}
						type="button"
						role="radio"
						aria-checked={selection === value}
						onClick={() => handleAmountChange(value)}
						className={selection === value ? BUTTON_SELECTED_CLASS : BUTTON_DEFAULT_CLASS}
					>
						{suggestedLabel && (
							<div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap [&>div>span>svg]:h-2.5">
								<Badge
									label={suggestedLabel}
									leadingIcon={<HeartFilledIcon size={8} />}
									size="tiny"
									variant="information"
								/>
							</div>
						)}
						{valueDisplay}
					</button>
				))}
				<button
					type="button"
					role="radio"
					aria-checked={selection === 'custom'}
					onClick={() => handleAmountChange('custom')}
					className={cn(selection === 'custom' ? BUTTON_SELECTED_CLASS : BUTTON_DEFAULT_CLASS, isGrid && 'col-span-3')}
				>
					{customAmountLabel}
				</button>
			</div>

			{selection === 'custom' && (
				<>
					<div className={CURRENCY_SYMBOL_POSITION_CLASS[currencySymbolPosition]}>
						<TextField
							label={customAmountLabel}
							labelIsVisible={false}
							name="customAmount"
							value={customAmount}
							inputMode="numeric"
							data-qa="custom-amount-input"
							isInvalid={!!amountErrorText}
							inputOptions={{
								onFocus: handleCustomAmountFocus,
								className:
									'mt-4 border-stroke-focus border-2 focus:border-transparent text-typography-accent-primary pl-6 h-12',
								onChange: handleCustomAmountChange,
								leadingIcon: currencySymbolPosition === 'before' ? <span>{currencySymbol}</span> : undefined,
								trailingIcon: currencySymbolPosition === 'after' ? <span>{currencySymbol}</span> : undefined,
							}}
						/>
					</div>
					{amountErrorText && (
						<div data-qa="custom-amount-input-error-message">
							<ErrorMessage size="small">{amountErrorText}</ErrorMessage>
						</div>
					)}
				</>
			)}
		</div>
	);
};
